import { Link } from 'react-router-dom'
import BurnCanvas from '../components/BurnCanvas'
import BurnBoardDemo from '../components/BurnBoardDemo'
import ScoreTicker from '../components/ScoreTicker'
import CountUp from '../components/CountUp'
import { LINEUP, INJURY_PROOF, STATS } from '../lib/landingData'

export default function Landing() {
  const total = LINEUP.reduce((a, l) => a + l.pts, 0)

  return (
    <div className="landing">
      <header className="landing-nav">
        <span className="wordmark">GRIDLOCK</span>
        <nav className="landing-nav-links">
          <a href="#how">How it works</a>
          <a href="#burn">The burn</a>
          <a href="#edge">Edge</a>
          <Link className="btn btn-ghost" to="/login">Sign in</Link>
        </nav>
      </header>

      {/* ---------------- hero ---------------- */}
      <section className="hero">
        <BurnCanvas />
        <div className="hero-inner">
          <div className="hero-copy">
            <p className="eyebrow">Fantasy football for people with jobs</p>
            <h1>Draft the room.<br />Not the guy.</h1>
            <p className="lede">
              Every week you build a lineup out of NFL position groups —
              <strong> CIN QB</strong>, <strong>DEN RB</strong>, <strong>DAL WR</strong>.
              You get the additive points of everyone at that position on that team.
              Starter goes down Sunday morning? Doesn't matter. You drafted the
              room, not the man.
            </p>
            <p className="lede">
              The catch: <strong>each unit is yours once all season.</strong> Spend
              KC QB in Week 2 and it's gone until January. Every pick blocks you
              forward.
            </p>
            <div className="cta-row">
              <Link className="btn btn-primary btn-lg" to="/login">Start a league — free</Link>
              <Link className="btn btn-ghost btn-lg" to="/app/join">I have a code</Link>
            </div>
            <p className="hero-fine">
              No draft night. No waivers. Six picks a week.
            </p>
          </div>

          <div className="hero-panel">
            <ScoreTicker />
          </div>
        </div>
      </section>

      {/* ---------------- a week ---------------- */}
      <section className="band" id="how">
        <div className="band-inner">
          <p className="eyebrow">A week takes four minutes</p>
          <h2>Six slots. Six rooms. Done.</h2>
          <p className="section-lede">
            This is a real Week 3 lineup and what it actually scored in 2025.
            Each pick locks at its own kickoff — a Thursday unit locks Thursday,
            the rest stay open until Sunday.
          </p>

          <div className="lineup-strip">
            {LINEUP.map((l) => (
              <div className="lu" key={l.slot}>
                <span className="lu-slot">{l.slot}</span>
                <span className="lu-unit">{l.unit}</span>
                <span className="lu-vs">{l.vs}</span>
                <span className="lu-pts">{l.pts.toFixed(1)}</span>
              </div>
            ))}
          </div>
          <div className="lineup-total">
            <span>Week 3 total</span>
            <strong>{total.toFixed(1)}</strong>
          </div>
        </div>
      </section>

      {/* ---------------- the burn ---------------- */}
      <section className="band band-alt" id="burn">
        <div className="band-inner">
          <p className="eyebrow">The burn</p>
          <h2>192 units. You'll spend 108 of them.</h2>
          <p className="section-lede">
            Thirty-two teams times six positions is the whole board. Eighteen
            weeks at six slots means you burn 18 of the 32 rooms at every
            position. Take the best option every single week and by Thanksgiving
            you're starting whatever's left. That's the game.
          </p>
          <BurnBoardDemo />
        </div>
      </section>

      {/* ---------------- injuries ---------------- */}
      <section className="band">
        <div className="band-inner">
          <p className="eyebrow">Injuries</p>
          <h2>The starter goes down<br />and you don't care.</h2>
          <p className="section-lede">
            A quarterback room where the starter left at halftime. In a normal
            league you started him, got 8.00, and the backup's 17.00 sat on a
            waiver wire you can't reach until Tuesday.
          </p>

          <div className="proof">
            <table className="proof-table">
              <thead>
                <tr>
                  <th>Cincinnati QB room, one Sunday</th>
                  <th>Yds</th><th>TD</th><th>INT</th><th>Points</th>
                </tr>
              </thead>
              <tbody>
                {INJURY_PROOF.map((r) => (
                  <tr key={r.who}>
                    <td>{r.who}</td>
                    <td>{r.yds}</td><td>{r.td}</td><td>{r.int}</td>
                    <td>{r.pts.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="proof-total">
                  <td>What <strong>CIN QB</strong> scored you</td>
                  <td>325</td><td>3</td><td>1</td><td>25.00</td>
                </tr>
              </tbody>
            </table>
            <p className="proof-note">
              You had the room, so you got all of it. Committee backfields work
              the same way — you own the whole committee.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- validation ---------------- */}
      <section className="band band-alt">
        <div className="band-inner">
          <p className="eyebrow">We tested it before we built it</p>
          <h2>The format holds up.</h2>
          <p className="section-lede">
            Two full seasons of real box scores, replayed week by week with the
            burn rule enforced. The worry was that summing a whole position room
            would flatten the scoring. It doesn't — unit scoring is slightly
            <em> wider</em> than traditional fantasy.
          </p>
          <div className="stats">
            {STATS.map((s) => (
              <div className="stat" key={s.label}>
                <div className="stat-n">
                  <CountUp
                    to={s.value}
                    decimals={s.value % 1 === 0 ? 0 : 1}
                    suffix={s.suffix}
                  />
                </div>
                <div className="stat-l">{s.label}</div>
                <div className="stat-s">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- pricing ---------------- */}
      <section className="band" id="edge">
        <div className="band-inner">
          <div className="price-grid">
            <div className="price">
              <h3>League play</h3>
              <p className="price-tag">Free<span>, forever</span></p>
              <ul className="ticks">
                <li>Unlimited leagues, six players or six hundred</li>
                <li>Weekly, season, and playoff scoring</li>
                <li>Commissioner scoring controls and position weights</li>
                <li>Hidden picks until kickoff</li>
              </ul>
              <Link className="btn btn-primary full" to="/login">Start a league</Link>
            </div>

            <div className="price price-edge">
              <h3>Gridlock Edge</h3>
              <p className="price-tag">$6<span>/month</span></p>
              <p className="price-pitch">
                Every fantasy site sells start/sit advice. Here a start/sit call
                is an <strong>allocation</strong> call — playing DEN RB in Week 3
                removes it from the other fifteen weeks. Edge prices each pick
                against your own remaining board.
              </p>
              <ul className="ticks">
                <li>A season-long burn plan, not a weekly hot take</li>
                <li>Every pick costed against the weeks it closes off</li>
                <li>Priced off the betting market, not last week's box score</li>
                <li>Playoff-reset planning</li>
              </ul>
              <p className="price-fine">You'll never need it to win.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="landing-foot">
        <div className="band-inner foot-inner">
          <span className="wordmark">GRIDLOCK</span>
          <span className="foot-note">
            Units, not dollars. No entry fees. What your league does about that
            is between you and your league.
          </span>
          <span className="foot-note">gridlockleague.com</span>
        </div>
      </footer>
    </div>
  )
}
